import { useState, useRef, useEffect } from 'react';
import { Button } from '~/design-system/elements/Button/Button';
import { DeckIcon } from '~/design-system/elements/DeckIcon/DeckIcon';
import { StreamingMarkdown } from './StreamingMarkdown';
import { WidgetSpinner } from './WidgetSpinner';
import { WidgetCollapse } from './WidgetCollapse';
import styles from './GoalWidgetUI.module.css';

/**
 * The goal CARD. The goal is something the agent produced, so it's one bordered object in the
 * thread — header, body and accept/reject inside a single border — not the borderless header+panel
 * shell used by the widgets that merely read a source (Linear / GitHub / Figma / web search).
 * Same family as CanvasWidgetUI and PlanWidgetUI.
 *
 * Bodies: loading shimmer → typewriter preview (scroll chrome + edge fades) → update word-diff →
 * settled markdown, all through the one WidgetCollapse everything else in the chat uses.
 */
export type GoalWidgetStatus = 'loading' | 'writing' | 'updating' | 'complete' | 'accepted' | 'rejected' | 'deleting' | 'deleted';

export interface GoalWidgetUIProps {
  status: GoalWidgetStatus;
  goalText?: string;
  previousGoalText?: string;
  defaultExpanded?: boolean;
  onNavigateToDocument?: () => void;
  onAccept?: () => void;
  onReject?: () => void;
  footerActionsDisabled?: boolean;
  hideFooter?: boolean;
}

const MinusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden xmlns="http://www.w3.org/2000/svg">
    <circle cx="8" cy="8" r="6.5" fill="#E5484D" />
    <path d="M5.2 8H10.8" stroke="#FFFFFF" strokeWidth="1.3" strokeLinecap="round" />
  </svg>
);

const GOAL_WRITING_PEEK_CHARS = 6;

/** Same “cascade” peek as the action timeline: settled markdown + short blurred lookahead. */
const TypewriterText = ({ text, scrollerRef }: { text: string; scrollerRef?: React.RefObject<HTMLDivElement | null> }) => {
  const prevLengthRef = useRef(0);
  const [settledLength, setSettledLength] = useState(0);

  useEffect(() => {
    if (text.length <= prevLengthRef.current) return;
    prevLengthRef.current = text.length;

    const timer = setTimeout(() => {
      setSettledLength(text.length);
    }, 80);

    if (scrollerRef?.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }

    return () => clearTimeout(timer);
  }, [text, scrollerRef]);

  const settled = text.slice(0, settledLength);
  const atEnd = settledLength >= text.length;
  const peek =
    !atEnd && text.length > settledLength
      ? text.slice(settledLength, settledLength + GOAL_WRITING_PEEK_CHARS)
      : '';

  return (
    <div className={styles.typingText}>
      <StreamingMarkdown content={settled} />
      {peek ? <span className={styles.typingUpcoming}>{peek}</span> : null}
    </div>
  );
};

type GoalScrollSize = 'writing' | 'expanded' | 'updating';

const GoalScrollChrome = ({
  size,
  scrollerRef,
  children,
}: {
  size: GoalScrollSize;
  scrollerRef?: React.RefObject<HTMLDivElement | null>;
  children: React.ReactNode;
}) => (
  <div className={styles.goalScrollZone} data-size={size}>
    <div className={styles.goalScrollScroller} ref={scrollerRef as React.Ref<HTMLDivElement>}>
      {children}
    </div>
    <div
      className={`${styles.goalScrollEdgeFade} ${styles.goalScrollEdgeFadeTop}`}
      aria-hidden
    />
    <div
      className={`${styles.goalScrollEdgeFade} ${styles.goalScrollEdgeFadeBottom}`}
      aria-hidden
    />
  </div>
);

const WritingBody = ({ text }: { text: string }) => {
  const scrollerRef = useRef<HTMLDivElement>(null);

  return (
    <div className={styles.writingBody}>
      <GoalScrollChrome size="writing" scrollerRef={scrollerRef}>
        <TypewriterText text={text} scrollerRef={scrollerRef} />
      </GoalScrollChrome>
    </div>
  );
};

type DiffSegment = { type: 'keep' | 'remove' | 'add'; text: string };

const computeWordDiff = (oldText: string, newText: string): DiffSegment[] => {
  const oldWords = oldText.split(/\s+/);
  const newWords = newText.split(/\s+/);
  const m = oldWords.length;
  const n = newWords.length;

  const dp: number[][] = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0));
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] = oldWords[i - 1] === newWords[j - 1]
        ? dp[i - 1][j - 1] + 1
        : Math.max(dp[i - 1][j], dp[i][j - 1]);
    }
  }

  const segments: DiffSegment[] = [];
  let i = m, j = n;
  const raw: { type: 'keep' | 'remove' | 'add'; word: string }[] = [];

  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && oldWords[i - 1] === newWords[j - 1]) {
      raw.push({ type: 'keep', word: oldWords[i - 1] });
      i--; j--;
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      raw.push({ type: 'add', word: newWords[j - 1] });
      j--;
    } else {
      raw.push({ type: 'remove', word: oldWords[i - 1] });
      i--;
    }
  }

  raw.reverse();

  for (const item of raw) {
    const last = segments[segments.length - 1];
    if (last && last.type === item.type) {
      last.text += ' ' + item.word;
    } else {
      segments.push({ type: item.type, text: item.word });
    }
  }

  return segments;
};

const InlineDiff = ({ oldText, newText }: { oldText: string; newText: string }) => {
  const segments = computeWordDiff(oldText, newText);

  return (
    <p className={styles.diffText}>
      {segments.map((seg, i) => {
        if (seg.type === 'remove') {
          return <span key={i} className={styles.diffRemoved}>{seg.text}</span>;
        }
        if (seg.type === 'add') {
          return <span key={i} className={styles.diffAdded}>{seg.text}</span>;
        }
        return <span key={i}>{seg.text} </span>;
      })}
    </p>
  );
};

const getLabel = (status: GoalWidgetStatus) => {
  switch (status) {
    case 'loading':
    case 'writing':
      return 'Writing goal';
    case 'updating':
      return 'Updating goal';
    case 'complete':
    case 'accepted':
    case 'rejected':
      return 'Goal';
    case 'deleting':
      return 'Removing goal';
    case 'deleted':
      return 'Goal removed';
  }
};

// Skeleton line widths for the loading state, varied so it reads as real prose.
const SHIMMER_LINES: number[][] = [[280, 80], [120, 200]];

export const GoalWidgetUI = ({
  status,
  goalText,
  previousGoalText,
  defaultExpanded = false,
  onNavigateToDocument,
  onAccept,
  onReject,
  footerActionsDisabled = false,
  hideFooter = false,
}: GoalWidgetUIProps) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const label = getLabel(status);
  const isComplete = status === 'complete';
  const isAccepted = status === 'accepted';
  const isRejected = status === 'rejected';
  const isDeleted = status === 'deleted';
  const isDeleting = status === 'deleting';
  const isLoading = status === 'loading';
  const isWriting = status === 'writing';
  const isUpdating = status === 'updating';
  const isSettled = isComplete || isAccepted || isRejected || isDeleting || isDeleted;
  // In-progress reads off the label shimmer + the header spinner, the same signal the source widgets
  // put on their active row.
  const isInProgress = isLoading || isWriting || isUpdating;

  const canExpand = isSettled && !!goalText;
  // Streaming bodies drive themselves open; settled ones follow the chevron.
  const showWriting = isWriting && !!goalText;
  const showUpdating = isUpdating && !!goalText && !!previousGoalText;
  const bodyOpen = isLoading || showWriting || showUpdating || (canExpand && isExpanded);
  const showFooter = (isComplete || isAccepted) && !hideFooter;

  const toggle = () => {
    if (!canExpand) return;
    setIsExpanded((prev) => !prev);
  };

  return (
    <div className={styles.card} data-status={status}>
      <button
        type="button"
        className={styles.header}
        data-clickable={canExpand ? 'true' : 'false'}
        data-rejected={isRejected ? 'true' : undefined}
        onClick={toggle}
      >
        <span className={styles.headerLeft}>
          <span className={styles.icon} data-lead-icon data-deleting={isDeleting ? 'true' : undefined}>
            <DeckIcon name="goal" size={15} />
          </span>
          <span
            className={styles.label}
            data-strikethrough={isDeleted || isRejected ? 'true' : undefined}
            data-shimmer={isInProgress || isDeleting ? 'true' : undefined}
            data-link={onNavigateToDocument ? 'true' : undefined}
            role={onNavigateToDocument ? 'link' : undefined}
            tabIndex={onNavigateToDocument ? 0 : undefined}
            onClick={(e) => {
              if (!onNavigateToDocument) return;
              e.stopPropagation();
              onNavigateToDocument();
            }}
            onKeyDown={(e) => {
              if (!onNavigateToDocument || (e.key !== 'Enter' && e.key !== ' ')) return;
              e.preventDefault();
              e.stopPropagation();
              onNavigateToDocument();
            }}
          >
            {label}
          </span>
        </span>
        <span className={styles.headerRight}>
          {isInProgress && (
            <span className={styles.headerStatus}>
              <WidgetSpinner />
            </span>
          )}
          {isDeleting && <span className={styles.statusDot} data-color="destructive" />}
          {isDeleted && (
            <span className={styles.minusBadge}>
              <MinusIcon />
            </span>
          )}
          {canExpand && (
            <span className={styles.chevron}>
              <DeckIcon name={isExpanded ? 'arrow-up-s' : 'arrow-down-s'} size={16} />
            </span>
          )}
        </span>
      </button>

      <WidgetCollapse open={bodyOpen}>
        <div className={styles.separator} />

        {isLoading && (
          <div className={styles.shimmerBody}>
            {SHIMMER_LINES.map((line, i) => (
              <div key={i} className={styles.shimmerLine}>
                {line.map((width, j) => (
                  <div key={j} className={styles.shimmerBlock} style={{ width }} />
                ))}
              </div>
            ))}
          </div>
        )}

        {showWriting && <WritingBody text={goalText!} />}

        {showUpdating && (
          <div className={styles.body}>
            <GoalScrollChrome size="updating">
              <InlineDiff oldText={previousGoalText!} newText={goalText!} />
            </GoalScrollChrome>
          </div>
        )}

        {isSettled && goalText && (
          <div className={styles.body} data-muted={isDeleting || isDeleted ? 'true' : undefined}>
            <StreamingMarkdown content={goalText} />
          </div>
        )}
      </WidgetCollapse>

      {showFooter && (
        <div className={styles.footer}>
          {isAccepted ? (
            <span className={styles.acceptedLabel}>
              <DeckIcon name="check" size={12} />
              Accepted
            </span>
          ) : (
            <>
              <Button
                type="button"
                variant="ghostMuted"
                size="xs"
                onClick={onReject}
                disabled={footerActionsDisabled}
                leadIcon={<DeckIcon name="close" size="button-xs" />}
              >
                Reject
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="xs"
                onClick={onAccept}
                disabled={footerActionsDisabled}
                leadIcon={<DeckIcon name="check" size="button-xs" />}
              >
                Accept
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
};
